import type { Identifier } from "ra-core";

/**
 * Almikia Epic E6 (TASK-039): row types of the invoicing tables and summary
 * views (TASK-037). Column names follow the E6 CONTRACT verbatim. Re-exported
 * from `../types` so consumers import everything from one place. Money is
 * always a decimal with two places (never float arithmetic: see
 * `invoicingRules.ts`); percentages carry up to four decimals.
 */

export const RECEIVABLE_STATUSES = [
  "pendiente_vobo",
  "por_facturar",
  "facturada",
  "cobrada",
  "cancelada",
] as const;
export type ReceivableStatus = (typeof RECEIVABLE_STATUSES)[number];

export const PAYABLE_STATUSES = [
  "pendiente_vobo",
  "por_pagar",
  "pagada",
  "cancelada",
] as const;
export type PayableStatus = (typeof PAYABLE_STATUSES)[number];

export const RECEIVABLE_PAYER_TYPES = ["developer", "client"] as const;
export type ReceivablePayerType = (typeof RECEIVABLE_PAYER_TYPES)[number];

export const PAYABLE_PAYEE_ROLES = [
  "asesora",
  "vendedora",
  "opcionadora",
  "colega",
] as const;
export type PayablePayeeRole = (typeof PAYABLE_PAYEE_ROLES)[number];

export const PAYABLE_BASES = [
  "bpo_scheme",
  "colega_fixed",
  "corretaje_split",
] as const;
export type PayableBasis = (typeof PAYABLE_BASES)[number];

export const ADVISOR_LEVEL_CODES = [
  "entrenamiento",
  "junior",
  "senior",
  "elite",
  "team_leader",
] as const;
export type AdvisorLevelCode = (typeof ADVISOR_LEVEL_CODES)[number];

/** A row of `public.receivables` (account receivable of Almikia, `receivables_summary` hides the `*_by` columns). */
export type Receivable = {
  id: Identifier;
  operation_id: string;
  installment_number: number;
  payer_type: ReceivablePayerType;
  developer_id?: Identifier | null;
  payer_name?: string | null;
  status: ReceivableStatus;
  /** Text snapshots taken at generation, so contabilidad never reads operations or clients. */
  project_code?: string | null;
  project_name?: string | null;
  unit_identifier?: string | null;
  client_name?: string | null;
  advisor_name?: string | null;
  sale_value?: number | null;
  commission_pct?: number | null;
  subtotal: number;
  iva_pct: number;
  iva_amount: number;
  total_amount: number;
  pct_edited_at_vobo: boolean;
  concept_text?: string | null;
  concept_template_version?: number | null;
  concept_frozen_at?: string | null;
  approved_by?: Identifier | null;
  approved_at?: string | null;
  invoice_folio?: string | null;
  invoiced_by?: Identifier | null;
  invoiced_at?: string | null;
  collected_by?: Identifier | null;
  collected_at?: string | null;
  last_rejection_reason?: string | null;
  last_rejected_by?: Identifier | null;
  last_rejected_at?: string | null;
  cancel_reason?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: Identifier | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
};

/** A row of the `receivables_summary` view: the receivable without the `*_by` columns. */
export type ReceivableSummary = Omit<
  Receivable,
  | "approved_by"
  | "invoiced_by"
  | "collected_by"
  | "last_rejected_by"
  | "cancelled_by"
  | "updated_by"
>;

/** A row of `public.payables` (account payable of Almikia to an advisor or a colega). */
export type Payable = {
  id: Identifier;
  operation_id: string;
  receivable_id?: Identifier | null;
  payee_role: PayablePayeeRole;
  /** A sales row for every role but `colega`. */
  payee_sales_id?: Identifier | null;
  payee_colega_id?: Identifier | null;
  payee_name?: string | null;
  project_code?: string | null;
  unit_identifier?: string | null;
  property_address?: string | null;
  basis: PayableBasis;
  level_code?: AdvisorLevelCode | null;
  rate_pct?: number | null;
  accumulated_sales?: number | null;
  base_amount?: number | null;
  subtotal: number;
  iva_pct: number;
  iva_amount: number;
  total_amount: number;
  status: PayableStatus;
  approved_by?: Identifier | null;
  approved_at?: string | null;
  paid_by?: Identifier | null;
  paid_at?: string | null;
  payment_reference?: string | null;
  invoice_document_id?: Identifier | null;
  last_rejection_reason?: string | null;
  last_rejected_by?: Identifier | null;
  last_rejected_at?: string | null;
  cancel_reason?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: Identifier | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
};

/** A row of the `payables_summary` view: the payable plus the linked receivable status (information badge). */
export type PayableSummary = Omit<
  Payable,
  "approved_by" | "paid_by" | "last_rejected_by" | "cancelled_by" | "updated_by"
> & {
  receivable_status?: ReceivableStatus | null;
};

/** A row of `public.advisor_commission_levels` (effective-dated catalogue, percent of sale value). */
export type AdvisorCommissionLevel = {
  id: Identifier;
  code: AdvisorLevelCode;
  name: string;
  /** NULL for `team_leader`: granted only through an override. */
  min_sales?: number | null;
  max_sales?: number | null;
  proactivo_pct: number;
  proactivo_step_pct: number;
  proactivo_cap_pct?: number | null;
  bpo_pct: number;
  bpo_step_pct: number;
  bpo_cap_pct?: number | null;
  with_colega_pct: number;
  valid_from: string;
  valid_to?: string | null;
};

/** A row of `public.advisor_level_overrides` (Team Leader, temporary promotions). */
export type AdvisorLevelOverride = {
  id: Identifier;
  sales_id: Identifier;
  level_code: AdvisorLevelCode;
  valid_from: string;
  valid_to?: string | null;
  reason: string;
  created_by?: Identifier | null;
  created_at: string;
};

/** A row of `public.invoice_concept_template_versions` (append-only history per project). */
export type InvoiceConceptTemplateVersion = {
  id: Identifier;
  project_id: Identifier;
  version: number;
  template: string;
  created_by?: Identifier | null;
  created_at: string;
};
